import { authClient } from 'lib/auth-client';

/**
 * Impersonation is a real BetterAuth session for the target user, so "am I
 * impersonating?" is a property of the session, not of local storage.
 *
 * It used to be an `x-impersonate` key that `apiFactory` turned into a
 * `Content-Language` header. That header only reached `/v2/api/*`, so anything
 * BetterAuth served — `/get-session`, `/link-social`, OAuth callbacks — kept
 * seeing the admin. The two halves disagreed silently, which is the bug this
 * replaced. Reading the session instead means there is one answer.
 */
export const useImpersonation = () => {
  const { data: session, isPending } = authClient.useSession();

  return {
    isPending,
    impersonatedBy: session?.session?.impersonatedBy ?? null,
    isImpersonating: !!session?.session?.impersonatedBy,
    impersonatedUserId: session?.session?.impersonatedBy
      ? (session.user?.id ?? null)
      : null,
  };
};

/**
 * BetterAuth's client resolves rather than throws — a failed call comes back as
 * `{ data: null, error }`. Left unchecked the caller reloads on a request that
 * did nothing, which reads as "the button does not work" instead of as an
 * error, so both of these turn it back into a rejection.
 */
const unwrap = async <T>(
  call: Promise<{ error?: { message?: string } | null; data?: T }>,
  fallback: string
) => {
  const { error } = await call;
  if (error) throw new Error(error.message || fallback);
};

/**
 * Switching straight from one user to another is supported and is the common
 * case for support work — the server authorizes against the admin behind the
 * session, not the user currently being worn.
 */
export const startImpersonating = async (userId: string) =>
  unwrap(
    authClient.impersonateUser({ userId }),
    'Could not start impersonating'
  );

export const stopImpersonating = async () =>
  unwrap(authClient.stopImpersonating(), 'Could not stop impersonating');
